import {
  effectiveNextReminderAt,
  firstReminderAt,
  reminderDelayDaysAfterSend,
} from './user-access-reminder-policy';

export type UserAccessRole = 'account_admin' | 'store_manager' | 'staff';
export type ExistingAccessAction = 'unset' | 'migrate' | 'remove';
export type ReviewCaseStatus = 'draft' | 'sent' | 'opened' | 'submitted' | 'tpb_reviewed' | 'completed' | 'blocked';
export type ReviewCaseFilter =
  | 'all'
  | 'needs_send'
  | 'waiting_on_store'
  | 'reminder_due'
  | 'submitted'
  | 'completed'
  | 'blocked';

export interface ExistingUserAccessRow {
  id: string;
  name: string;
  email: string;
  currentAccess: string;
  action: ExistingAccessAction;
  role: UserAccessRole | null;
  accountName: string;
  storeLocation: string;
  suggestedStoreLocations: string[];
}

export interface NewUserAccessRequest {
  id: string;
  name: string;
  email: string;
  role: UserAccessRole | null;
  accountName: string;
  storeLocation: string;
}

export interface ReviewDelegate {
  name: string;
  email: string;
}

export interface StoreReviewPacket {
  token: string;
  accountName: string;
  storeName: string;
  availableAccounts: string[];
  availableStoreLocations: string[];
  approver: {
    name: string;
    title: string;
    email: string;
  };
  existingUsers: ExistingUserAccessRow[];
  newUsers: NewUserAccessRequest[];
  notes: string;
}

export interface UserAccessReviewCase {
  id: string;
  environment?: 'production' | 'test';
  testRunId?: string | null;
  storeName: string;
  accountName: string;
  groupedLocations: string[];
  recipientGroupKey?: string | null;
  emailBundleSize?: number;
  emailBundleAccounts?: string[];
  token?: string;
  reviewUrl?: string | null;
  hasReviewToken?: boolean;
  approverName: string | null;
  approverEmail: string | null;
  recipientName?: string | null;
  recipientEmail?: string | null;
  dueAt: string;
  sentAt: string | null;
  openedAt: string | null;
  startedAt: string | null;
  draftSavedAt: string | null;
  submittedAt: string | null;
  lastReminderAt: string | null;
  nextReminderAt: string | null;
  reminderCount: number;
  delegatedAt: string | null;
  delegatedByEmail: string | null;
  delegationCount: number;
  status: ReviewCaseStatus;
  blockerSummary: string | null;
}

export interface UserAccessReviewSubmissionUser {
  id: string;
  rowKind: 'existing_v1_user' | 'new_v2_user';
  name: string;
  email: string;
  currentAccess: string | null;
  suggestedStoreLocations: string[];
  action: Exclude<ExistingAccessAction, 'unset'>;
  role: UserAccessRole | null;
  accountName: string | null;
  storeLocations: string[];
}

export interface UserAccessReviewSubmission {
  reviewCaseId: string;
  accountName: string;
  storeName: string;
  groupedLocations: string[];
  status: ReviewCaseStatus;
  submittedAt: string;
  approver: {
    name: string;
    title: string;
    email: string;
  };
  notes: string;
  existingUsers: UserAccessReviewSubmissionUser[];
  newUsers: UserAccessReviewSubmissionUser[];
}

export const USER_ACCESS_ROLES: Array<{ value: UserAccessRole; label: string; scopeLabel: string }> = [
  { value: 'account_admin', label: 'Account Admin', scopeLabel: 'Account-wide' },
  { value: 'store_manager', label: 'Store Manager', scopeLabel: 'One or more stores' },
  { value: 'staff', label: 'Staff', scopeLabel: 'One store' },
];

export const REVIEW_CASE_STATUS_LABELS: Record<ReviewCaseStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  opened: 'Opened',
  submitted: 'Submitted',
  tpb_reviewed: 'TPB reviewed',
  completed: 'Completed',
  blocked: 'Blocked',
};

export const REVIEW_CASE_FILTER_LABELS: Record<ReviewCaseFilter, string> = {
  all: 'All',
  needs_send: 'Needs send',
  waiting_on_store: 'Waiting on store',
  reminder_due: 'Reminder due',
  submitted: 'Submitted',
  completed: 'Completed',
  blocked: 'Blocked',
};

export const sampleStoreReviewPacket: StoreReviewPacket = {
  token: 'demo-token',
  accountName: 'Green Valley Collective',
  storeName: 'Green Valley locations',
  availableAccounts: ['Green Valley Collective'],
  availableStoreLocations: ['Green Valley - Downtown', 'Green Valley - North'],
  approver: {
    name: '',
    title: '',
    email: '',
  },
  notes: '',
  existingUsers: [
    {
      id: 'v1-user-maya',
      name: 'Maya Chen',
      email: 'maya.chen@greenvalley.example',
      currentAccess: 'Client Admin',
      action: 'migrate',
      role: 'account_admin',
      accountName: '',
      storeLocation: '',
      suggestedStoreLocations: [],
    },
    {
      id: 'v1-user-drew',
      name: 'Drew Patel',
      email: 'drew.patel@greenvalley.example',
      currentAccess: 'Store User',
      action: 'unset',
      role: null,
      accountName: '',
      storeLocation: '',
      suggestedStoreLocations: ['Green Valley - Downtown'],
    },
    {
      id: 'v1-user-sofia',
      name: 'Sofia Garcia',
      email: 'sofia.garcia@greenvalley.example',
      currentAccess: 'Marketing',
      action: 'unset',
      role: null,
      accountName: '',
      storeLocation: '',
      suggestedStoreLocations: ['Green Valley - North'],
    },
    {
      id: 'v1-user-jordan',
      name: 'Jordan Lee',
      email: 'jordan.lee@greenvalley.example',
      currentAccess: 'Client Admin',
      action: 'remove',
      role: null,
      accountName: 'Green Valley Collective',
      storeLocation: '',
      suggestedStoreLocations: [],
    },
  ],
  newUsers: [],
};

export const sampleReviewCases: UserAccessReviewCase[] = [
  {
    id: 'uar-green-valley-downtown',
    storeName: 'Green Valley - Downtown',
    accountName: 'Green Valley Collective',
    groupedLocations: ['Green Valley - Downtown', 'Green Valley - North'],
    token: 'demo-token',
    approverName: null,
    approverEmail: null,
    dueAt: '2026-07-26T23:59:00-07:00',
    sentAt: '2026-07-05T16:15:00-07:00',
    openedAt: '2026-07-06T09:20:00-07:00',
    startedAt: '2026-07-06T09:25:00-07:00',
    draftSavedAt: '2026-07-06T09:25:00-07:00',
    submittedAt: null,
    lastReminderAt: '2026-07-05T16:15:00-07:00',
    nextReminderAt: '2026-07-08T16:15:00-07:00',
    reminderCount: 1,
    delegatedAt: null,
    delegatedByEmail: null,
    delegationCount: 0,
    status: 'opened',
    blockerSummary: null,
  },
  {
    id: 'uar-green-valley-north',
    storeName: 'Green Valley - North',
    accountName: 'Green Valley Collective',
    groupedLocations: ['Green Valley - Downtown', 'Green Valley - North'],
    token: 'green-valley-north',
    approverName: 'Maya Chen',
    approverEmail: 'maya.chen@greenvalley.example',
    dueAt: '2026-07-26T23:59:00-07:00',
    sentAt: '2026-07-05T16:20:00-07:00',
    openedAt: '2026-07-06T10:05:00-07:00',
    startedAt: '2026-07-06T10:10:00-07:00',
    draftSavedAt: '2026-07-07T14:25:00-07:00',
    submittedAt: '2026-07-07T14:30:00-07:00',
    lastReminderAt: null,
    nextReminderAt: null,
    reminderCount: 0,
    delegatedAt: null,
    delegatedByEmail: null,
    delegationCount: 0,
    status: 'submitted',
    blockerSummary: null,
  },
  {
    id: 'uar-lazy-river-dracut',
    storeName: 'Lazy River - Dracut',
    accountName: 'Lazy River Products',
    groupedLocations: ['Lazy River - Dracut'],
    token: 'lazy-river-dracut',
    approverName: 'Taylor Knox',
    approverEmail: 'taylor.knox@lazyriver.example',
    dueAt: '2026-07-21T23:59:00-07:00',
    sentAt: null,
    openedAt: null,
    startedAt: null,
    draftSavedAt: null,
    submittedAt: null,
    lastReminderAt: null,
    nextReminderAt: null,
    reminderCount: 0,
    delegatedAt: null,
    delegatedByEmail: null,
    delegationCount: 0,
    status: 'draft',
    blockerSummary: null,
  },
  {
    id: 'uar-canna-planet',
    storeName: 'Canna Planet - Vista',
    accountName: 'Canna Planet',
    groupedLocations: ['Canna Planet - Vista'],
    token: 'canna-planet-vista',
    approverName: null,
    approverEmail: null,
    dueAt: '2026-07-14T23:59:00-07:00',
    sentAt: '2026-07-02T11:00:00-07:00',
    openedAt: null,
    startedAt: null,
    draftSavedAt: null,
    submittedAt: null,
    lastReminderAt: '2026-07-05T11:00:00-07:00',
    nextReminderAt: '2026-07-08T11:00:00-07:00',
    reminderCount: 2,
    delegatedAt: null,
    delegatedByEmail: null,
    delegationCount: 0,
    status: 'sent',
    blockerSummary: null,
  },
  {
    id: 'uar-sunset-market',
    storeName: 'Sunset Market - Palm',
    accountName: 'Sunset Market',
    groupedLocations: ['Sunset Market - Palm'],
    token: 'sunset-market-palm',
    approverName: 'Alex Rowe',
    approverEmail: 'alex@sunset.example',
    dueAt: '2026-07-16T23:59:00-07:00',
    sentAt: '2026-07-03T10:00:00-07:00',
    openedAt: '2026-07-03T13:20:00-07:00',
    startedAt: null,
    draftSavedAt: null,
    submittedAt: null,
    lastReminderAt: null,
    nextReminderAt: null,
    reminderCount: 0,
    delegatedAt: '2026-07-04T11:15:00-07:00',
    delegatedByEmail: 'owner@sunset.example',
    delegationCount: 1,
    status: 'blocked',
    blockerSummary: 'Store merge needs TPB review before customer can approve access.',
  },
  {
    id: 'uar-harvest-house',
    storeName: 'Harvest House - East',
    accountName: 'Harvest House',
    groupedLocations: ['Harvest House - East'],
    token: 'harvest-house-east',
    approverName: 'Priya Rao',
    approverEmail: 'priya@harvest.example',
    dueAt: '2026-07-12T23:59:00-07:00',
    sentAt: '2026-07-01T15:00:00-07:00',
    openedAt: '2026-07-02T08:45:00-07:00',
    startedAt: '2026-07-02T09:00:00-07:00',
    draftSavedAt: '2026-07-03T12:05:00-07:00',
    submittedAt: '2026-07-03T12:10:00-07:00',
    lastReminderAt: null,
    nextReminderAt: null,
    reminderCount: 0,
    delegatedAt: null,
    delegatedByEmail: null,
    delegationCount: 0,
    status: 'completed',
    blockerSummary: null,
  },
];

export function roleLabel(role: UserAccessRole | null): string {
  if (!role) return '';
  return USER_ACCESS_ROLES.find((item) => item.value === role)?.label ?? '';
}

export function isCustomerSelectableRole(value: string | null | undefined): value is UserAccessRole {
  return value === 'account_admin' || value === 'store_manager' || value === 'staff';
}

export function splitStoreLocations(value: string): string[] {
  return value
    .split(/[\n;,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function isReadOnlyReviewStatus(status: string | null | undefined): status is ReviewCaseStatus {
  return status === 'submitted' || status === 'tpb_reviewed' || status === 'completed' || status === 'blocked';
}

export function isReviewCaseStatus(status: string | null | undefined): status is ReviewCaseStatus {
  return status === 'draft' || status === 'sent' || status === 'opened' || isReadOnlyReviewStatus(status);
}

export function reviewedStoreSuggestion(row: Pick<ExistingUserAccessRow, 'suggestedStoreLocations'>): string | null {
  const suggestions = Array.from(
    new Set(row.suggestedStoreLocations.map((location) => location.trim()).filter(Boolean))
  );
  return suggestions.length === 1 ? suggestions[0] ?? null : null;
}

export function storeScopeForRole(
  role: UserAccessRole | null,
  currentValue: string,
  suggestedStoreLocations: string[] = []
): string {
  if (!role || role === 'account_admin') return '';
  const currentLocations = splitStoreLocations(currentValue);
  if (role === 'store_manager' && currentLocations.length > 0) return currentLocations.join('; ');
  if (role === 'staff' && currentLocations.length === 1) return currentLocations[0] ?? '';
  const suggestion = reviewedStoreSuggestion({ suggestedStoreLocations });
  return suggestion || '';
}

export function accessTargetsForRole(
  role: UserAccessRole,
  packetAccountName: string,
  currentStoreLocation: string,
  suggestedStoreLocations: string[] = []
): Pick<ExistingUserAccessRow, 'accountName' | 'storeLocation'> {
  if (role === 'account_admin') {
    return { accountName: '', storeLocation: '' };
  }
  return {
    accountName: packetAccountName,
    storeLocation: storeScopeForRole(role, currentStoreLocation, suggestedStoreLocations),
  };
}

function uniqueReviewOptions(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

export function accessTargetsForPacketRole(
  packet: Pick<StoreReviewPacket, 'accountName' | 'storeName' | 'availableStoreLocations'>,
  role: UserAccessRole,
  currentStoreLocation: string,
  suggestedStoreLocations: string[] = []
): Pick<ExistingUserAccessRow, 'accountName' | 'storeLocation'> {
  const targets = accessTargetsForRole(
    role,
    packet.accountName,
    currentStoreLocation,
    suggestedStoreLocations
  );
  const scopedStores = uniqueReviewOptions(packet.availableStoreLocations || []);
  const stores = scopedStores.length > 0 ? scopedStores : uniqueReviewOptions([packet.storeName]);
  const [onlyStore] = stores.length === 1 ? stores : [];

  if (role === 'account_admin') {
    return { accountName: packet.accountName.trim(), storeLocation: '' };
  }
  if (onlyStore) {
    return { ...targets, storeLocation: onlyStore };
  }
  return targets;
}

export function normalizeAutomaticReviewTargets(packet: StoreReviewPacket): StoreReviewPacket {
  return {
    ...packet,
    existingUsers: packet.existingUsers.map((user) => {
      if (user.action !== 'migrate' || !user.role) return user;
      return {
        ...user,
        ...accessTargetsForPacketRole(
          packet,
          user.role,
          user.storeLocation,
          user.suggestedStoreLocations
        ),
      };
    }),
    newUsers: packet.newUsers.map((user) => {
      if (!user.role) return user;
      return {
        ...user,
        ...accessTargetsForPacketRole(packet, user.role, user.storeLocation),
      };
    }),
  };
}

export function createNewUserAccessRequest(index: number): NewUserAccessRequest {
  return {
    id: `new-user-${Date.now()}-${index}`,
    name: '',
    email: '',
    role: null,
    accountName: '',
    storeLocation: '',
  };
}

export function normalizeUserEmail(value: string): string {
  return value.trim().toLowerCase();
}

export function duplicateNewUserEmail(
  packet: StoreReviewPacket,
  row: NewUserAccessRequest
): boolean {
  const email = normalizeUserEmail(row.email);
  if (!email) return false;
  return packet.existingUsers.some((existing) => normalizeUserEmail(existing.email) === email)
    || packet.newUsers.some((candidate) => candidate.id !== row.id && normalizeUserEmail(candidate.email) === email);
}

export function validateExistingUser(row: ExistingUserAccessRow): string[] {
  const errors: string[] = [];
  if (row.action === 'unset') errors.push('Choose Keep or Remove.');
  if (row.action === 'migrate' && !row.role) errors.push('Choose Account Admin, Store Manager, or Staff.');
  if (row.action === 'migrate' && row.role && !isCustomerSelectableRole(row.role)) {
    errors.push('Role must be Account Admin, Store Manager, or Staff.');
  }
  if (row.action === 'migrate' && row.role === 'account_admin' && !row.accountName.trim()) {
    errors.push('Account Admin requires an account name.');
  }
  if (row.action === 'migrate' && row.role === 'store_manager' && splitStoreLocations(row.storeLocation).length === 0) {
    errors.push('Store Manager requires at least one store location.');
  }
  if (row.action === 'migrate' && row.role === 'staff' && splitStoreLocations(row.storeLocation).length !== 1) {
    errors.push('Staff requires exactly one store location.');
  }
  return errors;
}

export function validateNewUser(row: NewUserAccessRequest, packet?: StoreReviewPacket): string[] {
  const errors: string[] = [];
  if (!row.name.trim()) errors.push('Name is required.');
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(row.email.trim())) errors.push('Valid email is required.');
  if (packet && duplicateNewUserEmail(packet, row)) errors.push('Email is already listed in this review.');
  if (!isCustomerSelectableRole(row.role)) errors.push('Role must be Account Admin, Store Manager, or Staff.');
  if (row.role === 'account_admin' && !row.accountName.trim()) errors.push('Account Admin requires an account name.');
  if (row.role === 'store_manager' && splitStoreLocations(row.storeLocation).length === 0) {
    errors.push('Store Manager requires at least one store location.');
  }
  if (row.role === 'staff' && splitStoreLocations(row.storeLocation).length !== 1) {
    errors.push('Staff requires exactly one store location.');
  }
  return errors;
}

export function approverErrors(approver: StoreReviewPacket['approver']): string[] {
  const errors: string[] = [];
  if (!approver.name.trim()) errors.push('Approver name is required.');
  if (!approver.title.trim()) errors.push('Approver title is required.');
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(approver.email.trim())) errors.push('Valid approver email is required.');
  return errors;
}

export function validateReviewDelegate(delegate: ReviewDelegate): string[] {
  const errors: string[] = [];
  if (!delegate.name.trim()) errors.push('Teammate name is required.');
  if (delegate.name.trim().length > 120) errors.push('Teammate name is too long.');
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(delegate.email.trim()) || delegate.email.trim().length > 320) {
    errors.push('Valid teammate email is required.');
  }
  return errors;
}

export function isReviewReady(packet: StoreReviewPacket): boolean {
  return packet.existingUsers.every((row) => validateExistingUser(row).length === 0)
    && packet.newUsers.every((row) => validateNewUser(row, packet).length === 0)
    && approverErrors(packet.approver).length === 0;
}

export function reviewProgress(packet: StoreReviewPacket): { completed: number; total: number; percent: number } {
  const completedExisting = packet.existingUsers.filter((row) => validateExistingUser(row).length === 0).length;
  const completedNew = packet.newUsers.filter((row) => validateNewUser(row, packet).length === 0).length;
  const total = packet.existingUsers.length + packet.newUsers.length;
  const completed = completedExisting + completedNew;
  return {
    completed,
    total,
    percent: total === 0 ? 0 : Math.round((completed / total) * 100),
  };
}

export function stopsReminder(status: ReviewCaseStatus): boolean {
  return status === 'submitted' || status === 'tpb_reviewed' || status === 'completed' || status === 'blocked';
}

export function isReminderDue(reviewCase: UserAccessReviewCase, now = new Date()): boolean {
  if (stopsReminder(reviewCase.status)) return false;
  if (reviewCase.status !== 'sent' && reviewCase.status !== 'opened') return false;
  const effectiveAt = effectiveNextReminderAt(reviewCase, now);
  return effectiveAt !== null && effectiveAt.getTime() <= now.getTime();
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

export function logReminderSent(reviewCase: UserAccessReviewCase, now = new Date()): UserAccessReviewCase {
  const reminderCount = reviewCase.reminderCount + 1;
  const delayDays = reminderDelayDaysAfterSend(
    reminderCount,
    reviewCase.startedAt,
    reviewCase.dueAt,
    now
  );
  return {
    ...reviewCase,
    lastReminderAt: now.toISOString(),
    nextReminderAt: addDays(now, delayDays).toISOString(),
    reminderCount,
  };
}

export function markCaseSent(reviewCase: UserAccessReviewCase, now = new Date()): UserAccessReviewCase {
  return {
    ...reviewCase,
    status: 'sent',
    sentAt: reviewCase.sentAt ?? now.toISOString(),
    nextReminderAt: reviewCase.nextReminderAt ?? firstReminderAt(now).toISOString(),
  };
}

export function markCaseStatus(
  reviewCase: UserAccessReviewCase,
  status: ReviewCaseStatus,
  now = new Date()
): UserAccessReviewCase {
  if (status === 'sent') return markCaseSent(reviewCase, now);
  if (status === 'completed' || status === 'blocked') {
    return {
      ...reviewCase,
      status,
      nextReminderAt: null,
    };
  }
  return {
    ...reviewCase,
    status,
  };
}

export function filterReviewCases(
  cases: UserAccessReviewCase[],
  filter: ReviewCaseFilter,
  now = new Date()
): UserAccessReviewCase[] {
  return cases.filter((reviewCase) => {
    if (filter === 'all') return true;
    if (filter === 'needs_send') return reviewCase.status === 'draft';
    if (filter === 'waiting_on_store') return reviewCase.status === 'sent' || reviewCase.status === 'opened';
    if (filter === 'reminder_due') return isReminderDue(reviewCase, now);
    if (filter === 'submitted') return reviewCase.status === 'submitted' || reviewCase.status === 'tpb_reviewed';
    return reviewCase.status === filter;
  });
}

export function reviewCaseCounts(cases: UserAccessReviewCase[], now = new Date()): Record<ReviewCaseFilter, number> {
  return {
    all: cases.length,
    needs_send: filterReviewCases(cases, 'needs_send', now).length,
    waiting_on_store: filterReviewCases(cases, 'waiting_on_store', now).length,
    reminder_due: filterReviewCases(cases, 'reminder_due', now).length,
    submitted: filterReviewCases(cases, 'submitted', now).length,
    completed: filterReviewCases(cases, 'completed', now).length,
    blocked: filterReviewCases(cases, 'blocked', now).length,
  };
}
